import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { useAppStore } from '@/stores/appStore'
import { useEarlyCareState, useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { durationBetween, relativeTime, toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { uploadAttachment } from '@/lib/attachments'
import { startNapLiveActivity, endNapLiveActivity } from '@/lib/liveActivity'
import { payloadValue, useEditableEvent } from '@/lib/editEvent'
import type { EventType } from '@/lib/types'

type SleepLocation = 'crib' | 'bassinet' | 'bed' | 'car' | 'carrier' | 'other'

export function SleepLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const state = useEarlyCareState(person?.id ?? null)
  const edit = useEditableEvent(['early_care.sleep.started', 'early_care.sleep.ended'])

  const isSleeping = state?.sleep_status === 'sleeping'
  const startedAt = state?.sleep_started_at ?? null

  const [at, setAt] = useState<string>(toLocalInputValue(new Date()))
  const [location, setLocation] = useState<SleepLocation>('crib')
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    const loc = payloadValue<SleepLocation>(p, 'location')
    if (loc) setLocation(loc)
    const n = payloadValue<string>(p, 'note')
    if (n) setNote(n)
    setAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  if (!person || !familyId || !user) return <Screen title="Sleep" back />

  const packColor = PACK_COLORS.early_care.light
  const isEditing = !!edit.original
  const editingStart = isEditing && edit.original!.event_type === 'early_care.sleep.started'
  const editingEnd = isEditing && edit.original!.event_type === 'early_care.sleep.ended'

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return

      // When editing, reuse the original event type. Otherwise toggle based on live sleep state.
      let eventType: EventType
      if (edit.original) {
        eventType = edit.original.event_type as EventType
      } else {
        eventType = isSleeping ? 'early_care.sleep.ended' : 'early_care.sleep.started'
      }

      const payload: Record<string, unknown> = {}
      // Location only applies to sleep.started.
      if (eventType === 'early_care.sleep.started') payload.location = location
      if (note.trim()) payload.note = note.trim()

      if (photos.length > 0) payload.has_attachment = true

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType,
        payload: payload as import('@/lib/types').JSONValue,
        occurredAt: new Date(at),
        supersedes: edit.supersedesId,
      })
      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }

      // Live Activity — start on sleep.started (nap begins), end on sleep.ended
      if (eventType === 'early_care.sleep.started') {
        void startNapLiveActivity({
          babyName: person.display_name,
          location: location,
          startedAtMs: new Date(at).getTime(),
        })
      } else if (eventType === 'early_care.sleep.ended') {
        void endNapLiveActivity()
      }

      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const title = isEditing
    ? (editingStart ? 'Correct sleep start' : 'Correct sleep end')
    : (isSleeping ? 'End sleep' : 'Start sleep')

  return (
    <Screen title={title} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{edit.error}</div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Correcting an earlier sleep entry. Saving creates a new entry that supersedes the original.
          </div>
        )}

        {isSleeping && !isEditing ? (
          <div className="rounded-r-sm border p-4" style={{ background: packColor + '18', borderColor: packColor + '55' }}>
            <Kicker color={packColor} live>
              Sleeping now
            </Kicker>
            <div className="mt-2 font-display text-hero">{durationBetween(startedAt!, null)}</div>
            <div className="mt-1 text-caption text-text-tertiary">Started {relativeTime(startedAt)}</div>
          </div>
        ) : (editingStart || (!isSleeping && !isEditing)) ? (
          <div>
            <Kicker>Where</Kicker>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(['crib', 'bassinet', 'bed', 'car', 'carrier', 'other'] as const).map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setLocation(loc)}
                  className="rounded-r-sm px-2 py-3 text-caption font-medium border transition-colors capitalize"
                  style={
                    location === loc
                      ? { background: packColor, color: '#0B0F0E', borderColor: packColor }
                      : { background: 'transparent', borderColor: 'rgba(255,255,255,0.16)', color: '#C4CBC6' }
                  }
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <Field label={editingEnd ? 'Woke at' : editingStart ? 'Started at' : (isSleeping ? 'Woke at' : 'Started at')}>
          <TextInput type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
        </Field>

        <Field label="Note (optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos (optional)"
          hint="A photo of baby sleeping — first bassinet night, cutest position, etc."
          compact
        />

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" packColor={packColor} loading={busy} className="w-full">
          {isEditing ? 'Save correction' : isSleeping ? 'End sleep' : 'Start sleep'}
        </Button>
      </form>
    </Screen>
  )
}
